import React, { useState, useId } from 'react';
import { Plus, Trash2, Send, RotateCcw, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';

/**
 * Solicitudes — Formulario de solicitud de documentos registrales.
 *
 * adaptada al diseño midnight/copper del sistema.
 *
 * Flujo:
 *  1. Llenar datos del solicitante
 *  2. Agregar uno o más documentos a la lista
 *  3. Seleccionar forma de entrega
 *  4. Enviar → recibe número de seguimiento
 */

const FORMAS_ENTREGA = [
  'Retiro en oficina',
  'Correo electrónico',
  'Envío a domicilio',
];

const TIPOS_DOCUMENTO = [
  'Copia de Escritura',
  'Copia de Hipoteca',
  'Certificado de Dominio Vigente',
  'Certificado de Hipotecas y Gravámenes',
  'Certificado de Prohibiciones',
  'Copia de Alzamiento',
  'Copia de Servidumbre',
  'Otro',
];

const REGISTROS = ['Propiedad', 'Hipotecas', 'Comercio', 'Aguas', 'Otro'];

const SOLICITANTE_VACIO = {
  rut_solicitante: '', nombre_solicitante: '', telefono: '',
  email: '', rut_boleta: '', direccion: '',
};

const ITEM_VACIO = {
  tipo_documento: TIPOS_DOCUMENTO[0], foja: '', vta: false,
  numero: '', anio: '', registro: REGISTROS[0], nombre_propietario: '',
};

export default function CreacionIndice() {
  const formId = useId();
  const [solicitante, setSolicitante] = useState(SOLICITANTE_VACIO);
  const [itemActual, setItemActual] = useState(ITEM_VACIO);
  const [items, setItems] = useState([]);
  const [entrega, setEntrega] = useState({
    forma_entrega: FORMAS_ENTREGA[0], observaciones: '',
  });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // número de seguimiento tras éxito

  const handleSolicitante = (e) =>
    setSolicitante((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleItem = (e) => {
    const { name, type, value, checked } = e.target;
    setItemActual((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  /** Agrega el ítem actual a la lista */
  const agregarItem = () => {
    if (!itemActual.tipo_documento) {
      toast.error('Selecciona el tipo de documento.');
      return;
    }
    setItems((prev) => [...prev, { ...itemActual, _id: Date.now() }]);
    setItemActual(ITEM_VACIO);
  };

  /** Elimina un ítem de la lista por su índice */
  const eliminarItem = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  /** Limpia todo el formulario */
  const limpiar = () => {
    setSolicitante(SOLICITANTE_VACIO);
    setItemActual(ITEM_VACIO);
    setItems([]);
    setEntrega({ forma_entrega: FORMAS_ENTREGA[0], observaciones: '' });
    setResultado(null);
  };

  /** Envía la solicitud al backend */
  const enviarSolicitud = async () => {
    if (items.length === 0) {
      toast.error('Agrega al menos un documento a la solicitud.');
      return;
    }
    if (!solicitante.rut_solicitante || !solicitante.nombre_solicitante || !solicitante.email) {
      toast.error('Completa los datos del solicitante (RUT, nombre y correo son obligatorios).');
      return;
    }

    setEnviando(true);
    try {
      const payload = {
        ...solicitante,
        ...entrega,
        items: items.map(({ _id, ...item }) => item), // Excluir key temporal
      };
      const { data } = await api.post('/solicitudes', payload);
      setResultado(data.numero_seguimiento);
      toast.success('Solicitud enviada correctamente.');
    } catch (err) {
      const msgs = err.response?.data?.errors;
      if (msgs) {
        Object.values(msgs).flat().forEach((m) => toast.error(m));
      } else {
        toast.error(err.response?.data?.message || 'Error al enviar la solicitud.');
      }
    } finally {
      setEnviando(false);
    }
  };

  // ── Estado de éxito ──────────────────────────────────────────────────────
  if (resultado) {
    return (
      <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)' }}>
        <div className="glass-card animate-scale-in" style={{ textAlign: 'center', maxWidth: 500, margin: '4rem auto' }}>
          <CheckCircle size={56} style={{ color: 'var(--success)', marginBottom: '1rem' }} />
          <h3>Solicitud enviada</h3>
          <span className="accent-line" style={{ margin: '0.75rem auto 1.5rem' }} />
          <p className="text-muted">Tu número de seguimiento es:</p>
          <div style={{
            background: 'rgba(184,115,51,0.15)',
            border: '1px solid var(--copper)',
            borderRadius: 'var(--radius-md)',
            padding: '1rem 2rem',
            fontSize: '1.4rem',
            fontWeight: 700,
            color: 'var(--copper-light)',
            letterSpacing: '0.1em',
            margin: '1rem 0 1.5rem',
          }}>
            {resultado}
          </div>
          <p className="text-muted" style={{ fontSize: '0.88rem', marginBottom: '2rem' }}>
            Guarda este número. Lo necesitarás para hacer seguimiento a tu solicitud.
          </p>
          <button className="btn btn-primary" onClick={limpiar}>
            Nueva Solicitud
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem clamp(16px,4vw,48px)' }}>

      {/* Encabezado */}
      <div className="animate-fade-in">
        <h2 className="section-title">Creación de Índice</h2>
        <span className="accent-line" />
        <p className="section-subtitle">
          Creación de índices de registros de propiedad inmobiliaria.
        </p>
      </div>

      {/* ─── Sección 2: Qué Solicita ──────────────────────────────── */}
      <div className="glass-card animate-fade-in" style={{ marginBottom: '1.5rem' }}>
        <h4 style={{ color: 'var(--copper-light)', marginBottom: '1.2rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          📋 Datos Indice
        </h4>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-tipodoc`}>Tipo Documento</label>
            <select id={`${formId}-tipodoc`} name="tipo_documento" value={itemActual.tipo_documento} onChange={handleItem}>
              {TIPOS_DOCUMENTO.map((t) => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-foja`}>Foja</label>
            <input id={`${formId}-foja`} name="foja" value={itemActual.foja} onChange={handleItem} placeholder="120" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-numero`}>Número</label>
            <input id={`${formId}-numero`} name="numero" value={itemActual.numero} onChange={handleItem} placeholder="345" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-anio`}>Año</label>
            <input id={`${formId}-anio`} name="anio" type="number" value={itemActual.anio} onChange={handleItem} placeholder="2023" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-registro`}>Registro</label>
            <select id={`${formId}-registro`} name="registro" value={itemActual.registro} onChange={handleItem}>
              {REGISTROS.map((r) => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-propietario`}>Nombre Propietario</label>
            <input id={`${formId}-propietario`} name="nombre_propietario" value={itemActual.nombre_propietario} onChange={handleItem} placeholder="González López" />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label htmlFor={`${formId}-propietario`}>Rut Propietario</label>
            <input id={`${formId}-propietario`} name="nombre_propietario" value={itemActual.nombre_propietario} onChange={handleItem} placeholder="González López" />
          </div>
        </div>
      </div>
      {/* ─── Acciones finales ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button className="btn btn-secondary" onClick={limpiar}>
          <RotateCcw size={15} /> Limpiar Todo
        </button>
        <button
          className="btn btn-primary btn-lg"
          onClick={enviarSolicitud}
          disabled={enviando || items.length === 0}
        >
          {enviando
            ? <><span className="spinner" /> Enviando...</>
            : <><Send size={16} /> Enviar Solicitud</>
          }
        </button>
      </div>
    </div>
  );
}
